import { apiClient } from './client';
import type { ApiSuccess, PaginationMeta } from '../types';

export type WorkOrderStatus = 'draft' | 'issued' | 'in_progress' | 'completed' | 'cancelled';

export interface EpcWorkOrderItem {
  id: number; categoryId: number | null; subCategoryId: number | null; description: string;
  unit: string | null; hsnCode: string | null; quantity: number; rate: number; taxPercent: number; amount: number; taxAmount: number; sequence: number;
}
export interface WoSowLineItem {
  description: string; unit: string | null; rate: number;
  boqCategoryId: number | null; boqCategoryName: string; hsnCode: string | null; taxPercent: number;
}
export interface VendorInfo {
  id: number; vendorName: string; gstNumber: string | null; panNumber: string | null;
  email: string | null; mobile: string | null; serviceCategory: string | null; address: string | null;
}
export interface WoLineInput {
  categoryId?: number | null; subCategoryId?: number | null; description: string;
  unit?: string | null; hsnCode?: string | null; quantity: number; rate: number; taxPercent?: number | null;
}

export interface EpcWorkOrder {
  id: number;
  workOrderCode: string;
  projectId: number;
  clientId?: number | null;
  siteId?: number | null;
  taxTotal?: number;
  items?: EpcWorkOrderItem[];
  milestoneId: number | null;
  milestoneName?: string;
  vendorId: number | null;
  vendorName?: string;
  technicianId: number | null;
  technicianName?: string;
  title: string;
  scope: string | null;
  quotationUrl?: string | null;
  orderValue: number;
  status: WorkOrderStatus;
  progressPct: number;
  startDate: string | null;
  dueDate: string | null;
  completedDate: string | null;
  issuedAt: string | null;
  remarks: string | null;
  createdAt: string;
}

interface Paginated<T> { data: T[]; meta: PaginationMeta }

export interface CreateWorkOrderInput {
  projectId: number;
  clientId?: number | null;
  siteId?: number | null;
  milestoneId?: number | null;
  vendorId?: number | null;
  technicianId?: number | null;
  title: string;
  scope?: string;
  quotationUrl?: string;
  orderValue?: number;
  items?: WoLineInput[];
  startDate?: string;
  dueDate?: string;
  remarks?: string;
}

export const epcWorkOrderApi = {
  async list(params: Record<string, unknown> = {}): Promise<Paginated<EpcWorkOrder>> {
    const { data } = await apiClient.get<ApiSuccess<EpcWorkOrder[]>>('/epc-work-orders', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  get: async (id: number) => (await apiClient.get<ApiSuccess<EpcWorkOrder>>(`/epc-work-orders/${id}`)).data.data,
  create: async (body: CreateWorkOrderInput) => (await apiClient.post<ApiSuccess<EpcWorkOrder>>('/epc-work-orders', body)).data.data,
  vendorInfo: async (vendorId: number) => (await apiClient.get<ApiSuccess<VendorInfo>>(`/epc-work-orders/vendor/${vendorId}/info`)).data.data,
  sowItems: async (clientId: number, sowCategoryId: number) =>
    (await apiClient.get<ApiSuccess<{ items: WoSowLineItem[] }>>('/epc-work-orders/sow-items', { params: { clientId, sowCategoryId } })).data.data,
  update: async (id: number, body: Partial<CreateWorkOrderInput>) => (await apiClient.put<ApiSuccess<EpcWorkOrder>>(`/epc-work-orders/${id}`, body)).data.data,
  remove: async (id: number) => (await apiClient.delete(`/epc-work-orders/${id}`)).data,
  issue: async (id: number) => (await apiClient.post<ApiSuccess<EpcWorkOrder>>(`/epc-work-orders/${id}/issue`, {})).data.data,
  setProgress: async (id: number, body: { status?: string; progressPct?: number; remarks?: string }) =>
    (await apiClient.post<ApiSuccess<EpcWorkOrder>>(`/epc-work-orders/${id}/progress`, body)).data.data,
  cancel: async (id: number, reason?: string) => (await apiClient.post<ApiSuccess<EpcWorkOrder>>(`/epc-work-orders/${id}/cancel`, { reason })).data.data,
};
